import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MuiTypography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import { FONT_WEIGHTS } from 'Theme';
import { capitalize } from '@material-ui/core';

const MUI_COLORS = [
  'initial',
  'inherit',
  'primary',
  'secondary',
  'textPrimary',
  'textSecondary',
  'error',
];

export const COLORS = [
  ...MUI_COLORS,
  'blueMuted',
  'accentBlue',
  'accentBlue2',
  'accentBlue9',
  'accentBlue13',
  'accentBlue19',
  'accentBlueMuted2',
  'oceana500',
  'oceana800',
  'accentGreen',
  'accentGreen2',
  'completeGreen',
  'naturalGreen',
  'lightGray',
  'mediumGray',
  'mediumDarkGray',
  'primaryGray',
  'primaryGray2',
  'primaryGray4',
  'darkGray',
  'red',
  'errorMain',
  'reviewPurple',
  'white',
  'orange',
  'seafoam',
  'black',
  'disabled',
];

export const WEIGHTS = [
  'default',
  ...Object.keys(FONT_WEIGHTS),
];

const reduceRootWeights = () => {
  return (acc, curr) => {
    const className = `rootFontWeight${capitalize(curr)}`;

    if (curr !== 'default') {
      acc[className] = {
        fontWeight: FONT_WEIGHTS[curr],
      };
    } else {
      acc[className] = {};
    }

    return acc;
  };
};

const styles = theme => ({
  underline: {
    textDecoration: 'underline',
  },
  root: {},
  rootColorMainBlue: {
    color: theme.palette.primary.main,
  },
  rootColorMainBlueMuted: {
    color: theme.palette.facet.primaryBlueMuted,
  },
  rootColorAccentBlue: {
    color: theme.palette.facet.accentBlue,
  },
  rootColorAccentBlue2: {
    color: theme.palette.facet.accentBlue2,
  },
  rootColorAccentBlue9: {
    color: theme.palette.facet.accentBlue9,
  },
  rootColorAccentBlue13: {
    color: theme.palette.facet.accentBlue13,
  },
  rootColorAccentBlue19: {
    color: theme.palette.facet.accentBlue19,
  },
  rootColorOceana500: {
    color: theme.palette.facet.oceana500,
  },
  rootColorOceana800: {
    color: theme.palette.facet.oceana800,
  },
  rootColorAccentBlueMuted2: {
    color: theme.palette.facet.accentBlueMuted2,
  },
  rootColorDarkGray: {
    color: theme.palette.facet.darkGray,
  },
  rootColorLightGray: {
    color: theme.palette.facet.lightGray,
  },
  rootColorMediumGray: {
    color: theme.palette.facet.mediumGray,
  },
  rootColorMediumDarkGray: {
    color: theme.palette.facet.mediumDarkGray,
  },
  rootColorDisabled: {
    color: theme.palette.action.disabled,
  },
  rootColorBlack: {
    color: theme.palette.common.black,
  },
  rootColorPrimaryGray: {
    color: theme.palette.facet.primaryGray,
  },
  rootColorPrimaryGray2: {
    color: theme.palette.facet.primaryGray2,
  },
  rootColorPrimaryGray4: {
    color: theme.palette.facet.primaryGray4,
  },
  rootColorRed: {
    color: theme.palette.facet.red,
  },
  rootColorErrorMain: {
    color: theme.palette.facet.facetDesignErrorMain,
  },
  rootColorWhite: {
    color: theme.palette.common.white,
  },
  rootColorOrange: {
    color: theme.palette.facet.orange,
  },
  rootColorSeafoam: {
    color: theme.palette.facet.seafoam,
  },
  rootColorAccentGreen: {
    color: theme.palette.facet.accentGreen,
  },
  rootColorAccentGreen2: {
    color: theme.palette.facet.accentGreen2,
  },
  rootColorNaturalGreen: {
    color: theme.palette.facet.naturalGreen,
  },
  ...WEIGHTS.reduce(reduceRootWeights(), {}),
});

const Typography = React.forwardRef((props, ref) => {
  const {
    className,
    classes,
    color,
    weight,
    underline,
    ...rest
  } = props;

  const colorClass = classNames({
    [classes.rootColorMainBlue]: color === 'blue',
    [classes.rootColorMainBlueMuted]: color === 'blueMuted',
    [classes.rootColorAccentBlue]: color === 'accentBlue',
    [classes.rootColorAccentBlue2]: color === 'accentBlue2',
    [classes.rootColorAccentBlue9]: color === 'accentBlue9',
    [classes.rootColorAccentBlue13]: color === 'accentBlue13',
    [classes.rootColorAccentBlue19]: color === 'accentBlue19',
    [classes.rootColorOceana500]: color === 'oceana500',
    [classes.rootColorOceana800]: color === 'oceana800',
    [classes.rootColorAccentBlueMuted2]: color === 'accentBlueMuted2',
    [classes.rootColorDarkGray]: color === 'darkGray',
    [classes.rootColorLightGray]: color === 'lightGray',
    [classes.rootColorMediumGray]: color === 'mediumGray',
    [classes.rootColorMediumDarkGray]: color === 'mediumDarkGray',
    [classes.rootColorDisabled]: color === 'disabled',
    [classes.rootColorBlack]: color === 'black',
    [classes.rootColorPrimaryGray]: color === 'primaryGray',
    [classes.rootColorPrimaryGray2]: color === 'primaryGray2',
    [classes.rootColorPrimaryGray4]: color === 'primaryGray4',
    [classes.rootColorRed]: color === 'red',
    [classes.rootColorErrorMain]: color === 'errorMain',
    [classes.rootColorWhite]: color === 'white',
    [classes.rootColorOrange]: color === 'orange',
    [classes.rootColorSeafoam]: color === 'seafoam',
    [classes.rootColorAccentGreen]: color === 'accentGreen',
    [classes.rootColorAccentGreen2]: color === 'accentGreen2',
    [classes.rootColorNaturalGreen]: color === 'naturalGreen',
  });

  const weightClass = classNames({
    [classes.rootFontWeightExtraBold]: weight === 'extraBold',
    [classes.rootFontWeightBold]: weight === 'bold',
    [classes.rootFontWeightMedium]: weight === 'medium',
    [classes.rootFontWeightBook]: weight === 'book',
    [classes.rootFontWeightDefault]: weight === 'default',
    [classes.rootFontWeightLight]: weight === 'light',
  });

  return (
    <MuiTypography
      className={classNames(colorClass, weightClass, classes.root, className, { [classes.underline]: underline })}
      color={MUI_COLORS.includes(color) ? color : 'inherit'}
      ref={ref}
      {...rest}
    />
  );
});

Typography.defaultProps = {
  className: '',
  color: 'inherit',
  weight: 'default',
  underline: false,
};

Typography.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  color: PropTypes.oneOf(COLORS),
  weight: PropTypes.oneOf(WEIGHTS),
  underline: PropTypes.bool,
};

export default withStyles(styles, { name: 'Typography' })(Typography);
